// Переходы между разделами без перезагрузки: боковая панель и списки остаются на месте, меняется только содержимое.
// Ссылки остаются обычными <a href>: открыть в новой вкладке можно как раньше, а простое нажатие перехватываем здесь.
import { boot, type Page } from "./boot";
import { Store } from "./lib/reactive";
import { currentChatStore } from "./store";   // store.ts тоже импортирует router.ts: здесь он нужен только при переходе

const PATHS: Partial<Record<string, Page>> = {
  "/": "chat", "/documents": "documents", "/my": "my", "/privacy": "privacy", "/dashboard": "dashboard", "/settings": "settings",
  "/admin": "admin",
};

// Те же заголовки, что даёт сервер (PAGE_TITLES в pravosvod/web/app.py). Заголовок чата ставит сам чат.
const TITLES: Record<Page, string> = {
  chat: "Правосвод — ИИ-помощник юриста", documents: "Шаблоны документов — Правосвод", my: "Мои документы — Правосвод",
  privacy: "Политика конфиденциальности — Правосвод", auth_problem: "Вход — Правосвод",
  dashboard: "Дашборд — Правосвод", settings: "Настройки — Правосвод", admin: "Администратор — Правосвод",
};

// visit — номер перехода: раздел рисуется заново при каждом переходе, даже в тот же самый («Новый чат» из чата).
export type Route = { page: Page; visit: number };
export const routeStore = new Store<Route>({ page: boot.page, visit: 0 });

// Раздел, который закрывается, успевает доделать своё (чат сохраняет переписку), пока он ещё на экране.
const leaving = new Set<() => void>();
export function onLeave(handler: () => void) {
  leaving.add(handler);
  return () => { leaving.delete(handler); };
}

const pageOf = (url: URL): Page | undefined => (url.origin === location.origin ? PATHS[url.pathname] : undefined);

function show(page: Page) {
  currentChatStore.set(page === "chat" ? new URLSearchParams(location.search).get("chat") : null);
  document.body.classList.remove("nav-open");
  for (const dialog of document.querySelectorAll("dialog[open]")) (dialog as HTMLDialogElement).close();
  document.title = TITLES[page];
  window.scrollTo(0, 0);
  document.querySelector(".content")?.scrollTo(0, 0);    // рядом открыт документ — прокручивается колонка, а не страница
  routeStore.set({ page, visit: routeStore.get().visit + 1 });
}

export function navigate(href: string) {
  const url = new URL(href, location.href);
  const page = pageOf(url);
  if (!page) {
    location.href = url.href;
    return;
  }
  for (const handler of [...leaving]) handler();
  if (url.href === location.href) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
  show(page);
}

export function startRouter() {
  history.scrollRestoration = "manual";   // раздел рисуется заново, поэтому прокрутку выставляем сами
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!link || link.hasAttribute("download") || (link.target && link.target !== "_self")) return;
    const url = new URL(link.href);
    if (!pageOf(url)) return;                                       // вход, файлы, API — обычным переходом
    if (url.hash && url.pathname === location.pathname && url.search === location.search) return;
    event.preventDefault();
    navigate(url.href);
  });
  // «Назад» и «Вперёд» в браузере: раздел и открытый в нём сценарий берутся из адреса.
  window.addEventListener("popstate", () => {
    const page = pageOf(new URL(location.href));
    if (!page) return location.reload();
    for (const handler of [...leaving]) handler();
    show(page);
  });
}
